import onScan from "onscan.js";
import ScannerModule from "@/store/modules/scanner";
import { getProductCategoryByBarcode } from "@/dexie/product-category";
import { DBErrors } from "@/constants/errors/db";
import BookingModule from "@/store/modules/booking";
import { PCTypes } from "@/types/db/product-category";
import { PSSType } from "@/types/db/product-slot-state";
import { createPSS } from "./product-slot-state";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { Currencies } from "@/constants/currency";
import { LogService } from "@/services/logging";
import PSSModule from "@/store/modules/pss";
import POSConfigModule from "@/store/modules/pos-config";

let attached = false;

//     // Alternative to document.addEventListener('scan')

export function attachScanner() {
  if (attached) {
    // scanner already attached
    LogService.info(`Scanner: already attached with attached options`, {
      params: onScan.getOptions(document),
    });
    return;
  }
  // Enable scan events for the entire document
  onScan.attachTo(document, {
    // suffixKeyCodes: [9, 13], // enter-key expected at the end of a scan
    suffixKeyCodes: [13], // enter-key expected at the end of a scan
    reactToPaste: false, // Compatibility to built-in scanners in paste-mode (as opposed to keyboard-mode)
    // reactToKeydown: true,
    // reactToKeyDown: true,
    minLength: 6,
    avgTimeByChar: 75,
    timeBeforeScanTest: 110,
    onScan: function (sCode: string, iQty: number) {
      // Alternative to document.addEventListener('scan')
      // ScanModule.setValue('');
      console.log("Scanned", sCode);
      LogService.info(`Scanner: scanner triggered, value: ${sCode}`);
      ScannerModule.setValue(sCode);
      ScannerModule.toggle();
    },
    onScanError: function (oDebug) {
      LogService.info(`onScanError triggered, error attached`, {
        params: oDebug,
      });
      // console.log(`onScanError triggered, error attached`);
      // console.log(oDebug);
    },
    keyCodeMapper: function (oEvent) {
      // Look for special keycodes or other event properties specific to
      // your scanner. Cause by default, onScan takes only letters and numbers
      if (
        ["-", "_", "=", ":", "{", "}", ",", '"', "'", "/", "."].includes(
          oEvent.key
        )
      ) {
        return oEvent.key;
      }
      // Fall back to the default decoder in all other cases
      return onScan.decodeKeyEvent(oEvent);
    },
  });
  attached = true;
}

export function detachScanner() {
  if (!attached) {
    return;
  }

  onScan.detachFrom(document);
  attached = false;
}

export function attachedVueElement() {
  ScannerModule.addListener();
  attachScanner();
}

export function detachVueElement() {
  ScannerModule.removeListener();

  if (ScannerModule.noListeners) {
    detachScanner();
  }
}

export async function addScannedProductToBasket(barcode: string) {
  // Lets proceed adding the corresponding price of retailOption entity with this barcode to the basket with Qty: 1

  // Get the product category of the retail option with this barcode from localDB
  const psc = await getProductCategoryByBarcode(barcode);
  if (!psc) {
    LogService.info(DBErrors.NotFoundElement);
    return;
  }

  const price = psc?.prices.find(
    (price) => price.retail.findIndex((ret) => ret.barcode === barcode) > -1
  );
  if (!price) {
    return;
  }

  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  // Create one pss
  const pssList = await createPSS({
    pssType:
      psc?.type === PCTypes[PCTypes.Retail]
        ? PSSType[PSSType.Retail]
        : PSSType[PSSType.Ticket],
    bookingUuid: BookingModule.selectedBookingUUID,
    priceId: price.id,
    categoryId: psc.id,
    priceValue: price.value,
    productCategory: psc,
    count: 1,
    sellerId: booking?.sellerId as number,
    addons: [],
    sendTicket: 1,
    token: null,
    mainSlot: null,
    packagePriceId: null,
    bookingReference: booking.reference,
  });

  // add them to current booking
  BookingModule.addPSS({
    bookingUUID: BookingModule.selectedBookingUUID,
    pssList,
  });
  // For each pss created lets add its extension
  for (const pss of pssList) {
    PSSModule.addPSSExtensions([{ psc, pss }]);
  }

  // If this the first PC of this booking then update the booking currency
  if (booking.pssList.length === 0) {
    // So the booking has the default currency (CHF), lets update it
    // (creating a new booking object to avoid updating the original object in place)
    const updatedBooking = Object.assign({}, booking);
    // Cast the currency string into a property of object type Currencies (USD, EUR, CHF..), then use it as an index Currencies[key]
    const key = psc.product.currency as keyof typeof Currencies;
    updatedBooking.currency = Currencies[key];
    BookingModule.updateBooking(updatedBooking);
  }
}
